import { useTranslations } from 'next-intl';
import type { CSSProperties } from 'react';

import { ButtonLink } from '@commons/components/Button';
import DrupalImage from '@commons/components/Image/Drupal';
import TopoPatternImage from '@commons/components/Image/TopoPattern';
import Text from '@commons/components/Text';
import Flex from '@commons/containers/Flex';
import { FEATURED_DISPLAY_IMAGE_SIZES } from '@commons/pagebuilder/nodes/images';

import type { CaseStudyNodeProps } from '.';

/**
 * CaseStudy node component for the Featured display type.
 */

const CaseStudyNodeFeatured = (props: CaseStudyNodeProps) => {
  const t = useTranslations();

  const { title, summary, authors, image, alias, format } = props;

  const formatLabel = format ? format.name : t('nodes.caseStudy.label');
  const upperTitle = t('commons.featuredType', { type: formatLabel });

  const imageStyles: CSSProperties = { aspectRatio: '1/1', objectFit: 'cover' };
  const renderedImage = image ? (
    <DrupalImage {...image} sizes={FEATURED_DISPLAY_IMAGE_SIZES} style={imageStyles} />
  ) : (
    <TopoPatternImage alt={title} sizes={FEATURED_DISPLAY_IMAGE_SIZES} style={imageStyles} />
  );

  return (
    <Flex gap="xl" justify="space-between" direction={{ xs: 'column-reverse', md: 'row' }}>
      <Flex gap="md" width={{ xs: 12, md: 6 }} direction="column">
        <Text as="h6" level="label-lg" transform="uppercase" marginBottom="none">
          {upperTitle}
        </Text>
        <Text as="h2" transform="uppercase" marginBottom="none">
          {title}
        </Text>
        {summary && (
          <Text level="body-lg" marginBottom="none">
            {summary}
          </Text>
        )}
        {authors && (
          <Text level="body-sm" marginBottom="none">
            {t('commons.by', { author: authors })}
          </Text>
        )}
        <ButtonLink href={alias} variant="primary">
          {t('commons.learnMore')}
        </ButtonLink>
      </Flex>
      <Flex.Item width={{ xs: 12, md: 6 }}>{renderedImage}</Flex.Item>
    </Flex>
  );
};

export default CaseStudyNodeFeatured;
